import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { Panel, PanelHeader, PanelBody } from '../components/ui/Panel';
import { KpiBand } from '../components/ui/Kpi';
import Pill from '../components/ui/Pill';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import { ErrorNotice, LoadingPanel, EmptyState, SkeletonRows } from '../components/ui/States';
import { fmt, dateShort, orDash } from '../lib/format';
import { canMutate } from '../lib/roles';
import { useAuth } from '../contexts/AuthContext';
import { confirmAction } from '../components/ui/Confirm';
import {
  EditionDetail,
  EditionSummary,
  FamilyField,
  FormsOverview,
  Question,
  ResponseRow,
  apiError,
  editionName,
} from '../lib/forms';

interface FamilyData {
  family: { key: string; name: string; identity: string; description: string | null };
  fields: FamilyField[];
  editions: EditionSummary[];
}

const FILTERS = [
  { key: 'unmatched', label: 'Unmatched' },
  { key: 'flagged', label: 'Odd answers' },
  { key: 'missing', label: 'Gone upstream' },
  { key: 'name_only', label: 'Name only' },
  { key: 'non_member', label: 'Not members' },
] as const;

const TYPES: FamilyField['type'][] = ['yes/no', 'choice', 'number', 'date', 'text'];
const inputClass = 'border border-line px-2 py-[6px] text-small bg-white';
const th = 'font-semibold text-small text-ink-2 text-left px-[14px] py-[10px]';

function decisionText(q: Question, fields: FamilyField[]) {
  if (q.isNew || q.decision === null) return 'Needs a decision';
  if (q.decision === '_extra') return 'Kept as extra answer';
  if (q.decision === '_skip') return 'Not stored';
  const f = fields.find((x) => x.key === q.decision);
  return f ? `→ ${f.label} (${f.type}${f.sensitive ? ', sensitive' : ''})` : `→ ${q.decision}`;
}

function QuestionRow({
  q,
  fields,
  editionId,
  mutable,
  onDone,
}: {
  q: Question;
  fields: FamilyField[];
  editionId: string;
  mutable: boolean;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(q.isNew);
  const [existing, setExisting] = useState('');
  const [adding, setAdding] = useState(false);
  const [field, setField] = useState({ label: q.label.slice(0, 120), type: '' as string, sensitive: false });

  const resolve = useMutation(
    async (body: Record<string, unknown>) =>
      (await api.post(`/forms/editions/${editionId}/questions/${q.id}`, body)).data.data,
    {
      onSuccess: () => {
        setAdding(false);
        onDone();
      },
    },
  );
  const compatible = fields.filter((f) => q.compatibleFields.includes(f.key));

  return (
    <div className={`px-5 py-3 border-b border-line last:border-b-0 ${q.isNew ? 'bg-selected' : ''}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-table">
            {q.section ? <span className="text-ink-2">{q.section} › </span> : null}
            {q.label}
          </div>
          <div className="text-small text-ink-2 mt-[2px]">
            {q.gfType}
            {q.choices ? ` · ${q.choices.length} choices` : ''} · {decisionText(q, fields)}
          </div>
        </div>
        {mutable && !open ? (
          <button className="font-semibold text-small text-ink-2 hover:text-ink shrink-0" onClick={() => setOpen(true)}>
            Change
          </button>
        ) : null}
      </div>
      {mutable && open ? (
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <Button size="sm" variant="outline" disabled={resolve.isLoading} onClick={() => setAdding((v) => !v)}>
            Add as family field
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={resolve.isLoading}
            onClick={() => resolve.mutate({ action: 'extra' })}
          >
            Keep as extra
          </Button>
          <select
            value={existing}
            onChange={(e) => setExisting(e.target.value)}
            className={inputClass}
            disabled={!compatible.length}
          >
            <option value="">{compatible.length ? 'Same as existing field…' : 'No compatible field'}</option>
            {compatible.map((f) => (
              <option key={f.key} value={f.key}>
                {f.label} ({f.type})
              </option>
            ))}
          </select>
          {existing ? (
            <Button
              size="sm"
              variant="outline"
              disabled={resolve.isLoading}
              onClick={() => resolve.mutate({ action: 'existing', key: existing })}
            >
              Map
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="ghost"
            disabled={resolve.isLoading}
            onClick={() => resolve.mutate({ action: 'skip' })}
          >
            Don’t store
          </Button>
          {!q.isNew ? (
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
              Close
            </Button>
          ) : null}
        </div>
      ) : null}
      {mutable && open && adding ? (
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <input
            value={field.label}
            onChange={(e) => setField({ ...field, label: e.target.value })}
            className={`${inputClass} w-[320px]`}
            placeholder="Field label"
          />
          <select
            value={field.type}
            onChange={(e) => setField({ ...field, type: e.target.value })}
            className={inputClass}
          >
            <option value="">Type from the question</option>
            {TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <label className="text-small flex items-center gap-1">
            <input
              type="checkbox"
              checked={field.sensitive}
              onChange={(e) => setField({ ...field, sensitive: e.target.checked })}
            />
            Sensitive
          </label>
          <Button
            size="sm"
            variant="primary"
            disabled={resolve.isLoading || !field.label.trim()}
            onClick={() =>
              resolve.mutate({
                action: 'field',
                label: field.label.trim(),
                type: field.type || undefined,
                sensitive: field.sensitive,
              })
            }
          >
            Add field
          </Button>
        </div>
      ) : null}
      {resolve.isError ? (
        <div className="mt-2">
          <ErrorNotice>{apiError(resolve.error, 'Could not save the decision.')}</ErrorNotice>
        </div>
      ) : null}
    </div>
  );
}

function EditionPanel({
  editionId,
  families,
  mutable,
  onChanged,
}: {
  editionId: string;
  families: { key: string; name: string }[];
  mutable: boolean;
  onChanged: () => void;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ label: '', year: '', cohort: '' });
  const [family, setFamily] = useState('');
  const [showAll, setShowAll] = useState(false);

  const { data, isLoading, error } = useQuery<EditionDetail>(
    ['forms-edition', editionId],
    async () => (await api.get(`/forms/editions/${editionId}`)).data.data,
  );
  const refresh = () => {
    queryClient.invalidateQueries(['forms-edition', editionId]);
    onChanged();
  };
  const save = useMutation(
    async () =>
      api.patch(`/forms/editions/${editionId}`, {
        label: form.label,
        year: form.year || null,
        cohort: form.cohort,
      }),
    {
      onSuccess: () => {
        setEditing(false);
        refresh();
      },
    },
  );
  const changeFamily = useMutation(async () => api.post(`/forms/editions/${editionId}/family`, { familyKey: family }), {
    onSuccess: refresh,
  });
  const ignore = useMutation(async () => api.post(`/forms/editions/${editionId}/ignore`), { onSuccess: refresh });
  const reprocess = useMutation(async () => (await api.post(`/forms/editions/${editionId}/reprocess`)).data.data, {
    onSuccess: refresh,
  });

  if (isLoading || !data) return <LoadingPanel label="Loading form…" height={160} />;
  if (error) return <ErrorNotice>Could not load this form.</ErrorNotice>;

  const pending = data.questions.filter((q) => q.isNew);
  const shown = showAll ? data.questions : pending;
  const actionError = save.error || changeFamily.error || ignore.error || reprocess.error;

  return (
    <Panel className="mt-6">
      <PanelHeader title={editionName(data)} note={`Form ${data.formId}`}>
        {data.upstreamActive ? <Pill tone="healthy">Open</Pill> : <Pill tone="inactive">Closed</Pill>}
      </PanelHeader>
      <PanelBody>
        <div className="text-small text-ink-2">
          {data.title}
          {data.year ? ` · ${data.year}` : ''}
          {data.cohort ? ` · cohort ${data.cohort}` : ''}
          {data.event
            ? ` · ${data.event.title}${data.event.startsAt ? ` (${dateShort(data.event.startsAt)})` : ''}`
            : ''}
        </div>
        {mutable ? (
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setForm({ label: data.label ?? '', year: String(data.year ?? ''), cohort: data.cohort ?? '' });
                setEditing((v) => !v);
              }}
            >
              Edit label
            </Button>
            <select value={family} onChange={(e) => setFamily(e.target.value)} className={inputClass}>
              <option value="">Change family…</option>
              {families
                .filter((f) => f.key !== data.family?.key)
                .map((f) => (
                  <option key={f.key} value={f.key}>
                    {f.name}
                  </option>
                ))}
            </select>
            {family ? (
              <Button
                size="sm"
                variant="outline"
                disabled={changeFamily.isLoading}
                onClick={() => changeFamily.mutate()}
              >
                {changeFamily.isLoading ? 'Moving…' : 'Move'}
              </Button>
            ) : null}
            <Button size="sm" variant="outline" disabled={reprocess.isLoading} onClick={() => reprocess.mutate()}>
              {reprocess.isLoading ? 'Reprocessing…' : 'Reprocess entries'}
            </Button>
            <Button
              size="sm"
              variant="destructive"
              disabled={ignore.isLoading}
              onClick={() => {
                void confirmAction(
                  `Ignore ${editionName(data)}? Its ${data.entries} stored entries are deleted from the platform; WordPress keeps them.`,
                ).then((ok) => ok && ignore.mutate());
              }}
            >
              Ignore form
            </Button>
          </div>
        ) : null}
        {editing ? (
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <input
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              className={`${inputClass} w-[300px]`}
              placeholder="Label"
            />
            <input
              value={form.year}
              onChange={(e) => setForm({ ...form, year: e.target.value.replace(/\D/g, '').slice(0, 4) })}
              className={`${inputClass} w-[80px]`}
              placeholder="Year"
            />
            <input
              value={form.cohort}
              onChange={(e) => setForm({ ...form, cohort: e.target.value })}
              className={`${inputClass} w-[120px]`}
              placeholder="Cohort"
            />
            <Button size="sm" variant="primary" disabled={save.isLoading} onClick={() => save.mutate()}>
              Save
            </Button>
          </div>
        ) : null}
        {reprocess.data ? (
          <p className="text-small text-ink-2 mt-2">Reprocessed {fmt(reprocess.data.processed)} entries.</p>
        ) : null}
        {actionError ? (
          <div className="mt-3">
            <ErrorNotice>{apiError(actionError, 'The change did not go through.')}</ErrorNotice>
          </div>
        ) : null}
      </PanelBody>
      <div className="border-t border-line-strong">
        <div className="flex items-center justify-between px-5 py-3 bg-paper border-b border-line">
          <span className="font-semibold text-small">
            {pending.length ? `${pending.length} new ${pending.length === 1 ? 'question' : 'questions'}` : 'Questions'}
          </span>
          <button className="font-semibold text-small link-underline" onClick={() => setShowAll((v) => !v)}>
            {showAll ? 'Show new only' : `Show all ${data.questions.length}`}
          </button>
        </div>
        {shown.length === 0 ? (
          <PanelBody>
            <p className="text-small text-ink-2">
              {data.questions.length
                ? 'Every question has a decision.'
                : 'The form definition has not been read from WordPress yet.'}
            </p>
          </PanelBody>
        ) : (
          shown.map((q) => (
            <QuestionRow
              key={q.id}
              q={q}
              fields={data.fields}
              editionId={editionId}
              mutable={mutable}
              onDone={refresh}
            />
          ))
        )}
      </div>
    </Panel>
  );
}

function Exceptions({ familyKey, mutable, onChanged }: { familyKey: string; mutable: boolean; onChanged: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<(typeof FILTERS)[number]['key']>('unmatched');
  const [linking, setLinking] = useState<ResponseRow | null>(null);
  const [linkEmail, setLinkEmail] = useState('');
  const [confirming, setConfirming] = useState<ResponseRow | null>(null);
  const [reason, setReason] = useState('');

  const list = useQuery<ResponseRow[]>(
    ['forms-responses', familyKey, filter],
    async () => (await api.get('/forms/responses', { params: { familyKey, filter, limit: 100 } })).data.data,
  );
  const refresh = () => {
    queryClient.invalidateQueries(['forms-responses', familyKey]);
    onChanged();
  };
  const guest = useMutation(async (id: string) => api.post(`/forms/responses/${id}/guest`), { onSuccess: refresh });
  const link = useMutation(async () => api.post(`/forms/responses/${linking?.id}/link`, { email: linkEmail }), {
    onSuccess: () => {
      setLinking(null);
      setLinkEmail('');
      refresh();
    },
  });
  const missing = useMutation(
    async ({ id, action }: { id: string; action: 'confirm' | 'dismiss' }) =>
      api.post(`/forms/responses/${id}/missing`, { action, reason }),
    {
      onSuccess: () => {
        setConfirming(null);
        setReason('');
        refresh();
      },
    },
  );

  return (
    <Panel className="mt-6">
      <PanelHeader title="Exceptions">
        <div className="flex gap-1">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`font-semibold text-small px-2 py-[5px] border ${
                filter === f.key ? 'bg-ink text-paper border-ink' : 'border-line text-ink-2 hover:text-ink'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </PanelHeader>
      {filter === 'name_only' ? (
        <div className="px-5 pt-3 text-small text-ink-2">
          These responses carry a name only. IDP, ToF and trainee-survey responses link automatically when the name
          matches exactly one member of the Fellow group; the rest (no match, or two Fellows with that name) stay here
          to link by hand.
        </div>
      ) : null}
      {list.isLoading ? (
        <SkeletonRows rows={4} />
      ) : (list.data ?? []).length === 0 ? (
        <EmptyState title="Nothing here">No responses in this family match this filter.</EmptyState>
      ) : (
        <table className="w-full">
          <thead>
            <tr className="bg-paper border-b border-line">
              <th className={`${th} px-5`}>Respondent</th>
              <th className={th}>Form</th>
              <th className={th}>Submitted</th>
              <th className={th}>Detail</th>
              <th className={`${th} text-right px-5`} />
            </tr>
          </thead>
          <tbody>
            {(list.data ?? []).map((r) => (
              <tr key={r.id} className="border-b border-line last:border-b-0 align-top">
                <td className="px-5 py-3">
                  {r.member ? (
                    <button
                      className="text-table font-semibold link-underline"
                      onClick={() => navigate(`/analytics/members/${r.member!.id}`)}
                    >
                      {r.member.firstName} {r.member.lastName}
                    </button>
                  ) : (
                    <div className="text-table font-semibold">{orDash(r.name)}</div>
                  )}
                  <div className="text-small text-ink-2">{orDash(r.email)}</div>
                </td>
                <td className="px-[14px] py-3 text-small">
                  {r.edition}
                  <div className="text-small text-ink-2">entry {r.entryId}</div>
                </td>
                <td className="px-[14px] py-3 text-small tabular">{dateShort(r.submittedAt)}</td>
                <td className="px-[14px] py-3 text-small text-ink-2 max-w-[280px]">
                  {filter === 'flagged'
                    ? r.flags
                        .map(
                          (f) =>
                            `${f.field}: “${Array.isArray(f.value) ? f.value.join(', ') : String(f.value)}” ${f.reason}`,
                        )
                        .join('; ')
                    : filter === 'missing'
                      ? `Missing since ${dateShort(r.upstreamMissingSince)} · ${r.upstreamMissingRuns} runs`
                      : r.matchStatus.replace('_', ' ')}
                </td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  {mutable && filter === 'unmatched' ? (
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => setLinking(r)}>
                        Link
                      </Button>
                      <Button size="sm" variant="ghost" disabled={guest.isLoading} onClick={() => guest.mutate(r.id)}>
                        Guest
                      </Button>
                    </div>
                  ) : null}
                  {mutable && filter === 'missing' ? (
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="destructive" onClick={() => setConfirming(r)}>
                        Confirm deletion
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={missing.isLoading}
                        onClick={() => missing.mutate({ id: r.id, action: 'dismiss' })}
                      >
                        Dismiss
                      </Button>
                    </div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {linking ? (
        <Modal
          title="Link to a member"
          onClose={() => setLinking(null)}
          footer={
            <>
              <Button variant="primary" disabled={!linkEmail.trim() || link.isLoading} onClick={() => link.mutate()}>
                Link
              </Button>
              <Button variant="ghost" onClick={() => setLinking(null)}>
                Cancel
              </Button>
            </>
          }
        >
          <p className="text-table">
            {orDash(linking.name)} submitted {linking.edition} as {orDash(linking.email)}. Enter the email the member is
            registered under.
          </p>
          <input
            value={linkEmail}
            onChange={(e) => setLinkEmail(e.target.value)}
            placeholder="member email"
            className="mt-4 w-full border border-line-strong px-3 py-2 text-table"
          />
          {link.isError ? (
            <div className="mt-4">
              <ErrorNotice>{apiError(link.error, 'Could not link.')}</ErrorNotice>
            </div>
          ) : null}
        </Modal>
      ) : null}

      {confirming ? (
        <Modal
          title="Confirm deletion"
          onClose={() => setConfirming(null)}
          footer={
            <>
              <Button
                variant="destructive"
                disabled={!reason.trim() || missing.isLoading}
                onClick={() => missing.mutate({ id: confirming.id, action: 'confirm' })}
              >
                Delete from the platform
              </Button>
              <Button variant="ghost" onClick={() => setConfirming(null)}>
                Cancel
              </Button>
            </>
          }
        >
          <p className="text-table">
            Entry {confirming.entryId} of {confirming.edition} is no longer in WordPress. Deleting removes the
            platform’s copy permanently; the decision is logged with your name and reason.
          </p>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (required), e.g. erasure request"
            className="mt-4 w-full border border-line-strong px-3 py-2 text-table"
          />
          {missing.isError ? (
            <div className="mt-4">
              <ErrorNotice>{apiError(missing.error, 'Could not delete.')}</ErrorNotice>
            </div>
          ) : null}
        </Modal>
      ) : null}
    </Panel>
  );
}

export default function FormFamily() {
  const { familyKey = '' } = useParams<{ familyKey: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const mutable = canMutate(user?.role);
  const selected = params.get('edition');

  const { data, isLoading, error } = useQuery<FamilyData>(
    ['forms-family', familyKey],
    async () => (await api.get(`/forms/families/${familyKey}`)).data.data,
  );
  const overview = useQuery<FormsOverview>('forms-overview', async () => (await api.get('/forms/overview')).data.data);
  const families = overview.data?.families ?? [];

  const refresh = () => {
    queryClient.invalidateQueries(['forms-family', familyKey]);
    queryClient.invalidateQueries('forms-overview');
    queryClient.invalidateQueries('dashboard');
  };

  const totals = (data?.editions ?? []).reduce(
    (acc, e) => ({
      entries: acc.entries + e.entries,
      matched: acc.matched + e.matched,
      unmatched: acc.unmatched + e.unmatched,
      newQuestions: acc.newQuestions + e.newQuestions,
    }),
    { entries: 0, matched: 0, unmatched: 0, newQuestions: 0 },
  );

  if (error) return <ErrorNotice>Could not load this family.</ErrorNotice>;

  return (
    <>
      <div className="mb-6">
        <button onClick={() => navigate('/forms')} className="font-semibold text-small text-ink-2 hover:text-ink">
          ← Website forms
        </button>
        <h1 className="font-serif font-semibold text-[42px] leading-none mt-3">{data?.family.name ?? '…'}</h1>
        {data?.family.description ? (
          <p className="text-table text-ink-2 mt-2 max-w-[80ch]">{data.family.description}</p>
        ) : null}
      </div>

      <KpiBand
        loading={isLoading}
        cells={[
          { label: 'Entries', value: fmt(totals.entries), note: `${data?.editions.length ?? 0} editions` },
          {
            label: 'Linked to a member',
            value: fmt(totals.matched),
            note:
              data?.family.identity === 'name'
                ? 'Identified by name; matching comes with the Fellow cohort'
                : 'Matched on email',
          },
          { label: 'Unmatched', value: fmt(totals.unmatched), note: 'Email not on any member' },
          { label: 'New questions', value: fmt(totals.newQuestions), note: 'Need a decision' },
        ]}
      />

      <Panel className="mt-7">
        <PanelHeader title="Editions" note="One WordPress form each" />
        {isLoading || !data ? (
          <SkeletonRows rows={5} />
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-paper border-b border-line">
                <th className={`${th} px-5`}>Edition</th>
                <th className={th}>Event</th>
                <th className={`${th} text-right`}>Entries</th>
                <th className={th}>Last entry</th>
                <th className={th}>To resolve</th>
                <th className={`${th} px-5`}>Form</th>
              </tr>
            </thead>
            <tbody>
              {data.editions.map((e) => (
                <tr
                  key={e.id}
                  onClick={() => setParams(selected === e.id ? {} : { edition: e.id })}
                  className={`border-b border-line last:border-b-0 cursor-pointer hover:bg-selected ${selected === e.id ? 'bg-selected' : ''}`}
                >
                  <td className="px-5 py-3">
                    <div className="text-table font-semibold">{editionName(e)}</div>
                    <div className="text-small text-ink-2">
                      {[e.year, e.cohort ? `cohort ${e.cohort}` : null].filter(Boolean).join(' · ') || '—'}
                    </div>
                  </td>
                  <td className="px-[14px] py-3 text-small">{e.event ? e.event.title : '—'}</td>
                  <td className="px-[14px] py-3 text-table text-right tabular">
                    {fmt(e.entries)}
                    {e.upstreamEntryCount !== null && e.upstreamEntryCount !== e.entries ? (
                      <div className="text-small text-ink-2">{fmt(e.upstreamEntryCount)} in WordPress</div>
                    ) : null}
                  </td>
                  <td className="px-[14px] py-3 text-small tabular">{dateShort(e.lastEntryAt)}</td>
                  <td className="px-[14px] py-3 text-small">
                    {[
                      e.newQuestions ? `${e.newQuestions} new questions` : null,
                      e.unmatched ? `${e.unmatched} unmatched` : null,
                      e.flagged ? `${e.flagged} odd answers` : null,
                      e.missing ? `${e.missing} gone upstream` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ') || <span className="text-ink-2">—</span>}
                  </td>
                  <td className="px-5 py-3 text-small text-ink-2 whitespace-nowrap">
                    {e.formId} {e.upstreamActive ? <Pill tone="healthy">Open</Pill> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      {selected ? (
        <EditionPanel key={selected} editionId={selected} families={families} mutable={mutable} onChanged={refresh} />
      ) : null}

      {data ? <Exceptions familyKey={familyKey} mutable={mutable} onChanged={refresh} /> : null}

      {data ? (
        <Panel className="mt-6">
          <PanelHeader title="Fields" note={`${data.fields.length} fields · answers are stored by these keys`} />
          <div className="grid gap-px bg-line" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            {data.fields.map((f) => (
              <div key={f.key} className="bg-white px-4 py-3">
                <div className="text-small">{f.label}</div>
                <div className="text-small text-ink-2 mt-[2px]">
                  {f.key} · {f.type}
                  {f.multiple ? ' (many)' : ''}
                  {f.sensitive ? ' · sensitive' : ''}
                </div>
              </div>
            ))}
          </div>
        </Panel>
      ) : null}
    </>
  );
}
